/* Prink: one drawer implementation, delegated close handling and section-only updates. */
(() => {
  if (!customElements.get('cart-drawer')) {
    class CartDrawer extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('keyup', (event) => {
          if (event.code === 'Escape' || event.key === 'Escape') this.close();
        });
        // The overlay is replaced after cart updates. Delegation survives replacement.
        this.addEventListener('click', (event) => {
          if (event.target.id === 'CartDrawer-Overlay') this.close();
        });
        this.setHeaderCartIconAccessibility();
      }
      setHeaderCartIconAccessibility() {
        const link = document.getElementById('cart-icon-bubble');
        if (!link || link === this.cartLink) return;
        this.cartLink = link;
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(link);
        });
        link.addEventListener('keydown', (event) => {
          if (event.code === 'Space' || event.key === ' ') {
            event.preventDefault();
            this.open(link);
          }
        });
      }
      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        this.classList.toggle('is-empty', !this.querySelector('.cart-item'));
        const note = this.querySelector('[id^="Details-"] summary');
        if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
        clearTimeout(this.openTimer);
        this.openTimer = setTimeout(() => {
          this.classList.add('animate', 'active');
          const items = this.querySelector('cart-drawer-items');
          if (items) {
            items.updateCartUpsellToggleState?.();
            const count = Array.from(this.querySelectorAll('input[data-quantity-variant-id]'))
              .reduce((total, input) => total + (Number(input.value) || 0), 0);
            items.updateCartUpsellVisibility?.(count);
          }
          const container = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : this.querySelector('.drawer__inner');
          if (container && typeof trapFocus === 'function') {
            trapFocus(container, container.querySelector('.drawer__close') || container);
          }
          this.dispatchEvent(new CustomEvent('prink:cart-opened', { bubbles: true }));
        });
        document.body.classList.add('overflow-hidden');
      }
      close() {
        clearTimeout(this.openTimer);
        this.classList.remove('active');
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : this.cartLink);
        }
        document.body.classList.remove('overflow-hidden');
      }
      setSummaryAccessibility(note) {
        note.setAttribute('role', 'button');
        note.setAttribute('aria-expanded', String(note.closest('details').open));
        if (note.nextElementSibling?.id) note.setAttribute('aria-controls', note.nextElementSibling.id);
        note.addEventListener('click', (event) => {
          event.currentTarget.setAttribute('aria-expanded', String(!event.currentTarget.closest('details').open));
        });
        if (typeof onKeyUpEscape === 'function') note.parentElement.addEventListener('keyup', onKeyUpEscape);
      }
      renderContents(state, shouldOpen = true) {
        const sections = state?.sections;
        const rawDrawer = sections?.['cart-drawer'];
        const rawBubble = sections?.['cart-icon-bubble'];
        if (typeof rawDrawer !== 'string' || typeof rawBubble !== 'string') {
          throw new Error('Cart updated, but its preview could not be refreshed. Open your cart to review it.');
        }
        const fresh = this.getSectionDOM(rawDrawer, '#CartDrawer');
        const freshBubbleDoc = new DOMParser().parseFromString(rawBubble, 'text/html');
        const freshBubble = freshBubbleDoc.querySelector('#cart-icon-bubble') || freshBubbleDoc.querySelector('.shopify-section');
        const current = this.querySelector('#CartDrawer');
        const bubble = document.getElementById('cart-icon-bubble');
        if (!fresh || !current || !freshBubble || !bubble) {
          throw new Error('Cart updated, but its preview could not be refreshed. Open your cart to review it.');
        }
        // Validate all sections before replacing either one.
        current.innerHTML = fresh.innerHTML;
        bubble.innerHTML = freshBubble.innerHTML;
        this.productId = state.id;
        this.classList.toggle('is-empty', !this.querySelector('.cart-item'));
        this.dispatchEvent(new CustomEvent('prink:cart-rendered', { bubbles: true, detail: { sections } }));
        if (shouldOpen) this.open();
      }
      getSectionInnerHTML(html, selector = '.shopify-section') {
        const node = this.getSectionDOM(html, selector);
        if (!node) throw new Error('Missing cart section');
        return node.innerHTML;
      }
      getSectionDOM(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      }
      getSectionsToRender() {
        return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
      }
      setActiveElement(element) { this.activeElement = element; }
    }
    customElements.define('cart-drawer', CartDrawer);
  }
  if (!customElements.get('cart-drawer-items')) {
    class CartDrawerItems extends CartItems {
      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // These callers already render the sections returned by their cart mutation.
          if (event.source === 'cart-items' || event.cartData?.sections?.['cart-drawer']) return;
          this.onCartUpdate();
        });
      }
      async onCartUpdate() {
        const drawer = this.closest('cart-drawer');
        if (!drawer) return;
        try {
          const url = new URL(window.routes.cart_url, window.location.origin);
          url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
          const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(window.cartStrings.error);
          const sections = await response.json();
          drawer.renderContents({ sections }, drawer.classList.contains('active'));
        } catch (error) {
          console.error(error);
          const errorNode = drawer.querySelector('#CartDrawer-CartErrors');
          if (errorNode) errorNode.textContent = window.cartStrings.error;
        }
      }
      getSectionsToRender() {
        return [
          { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ];
      }
    }
    customElements.define('cart-drawer-items', CartDrawerItems);
  }
})();
